import {useLazyQuery, useMutation, useReactiveVar} from '@apollo/client'
import {SET_BILLING_ADDRESS_ON_CART} from '_app/graphql/mutations/cart/SetBillingAddressOnCart'
import {SET_GUEST_EMAIL_ON_CART} from '_app/graphql/mutations/cart/SetGuestEmailOnCart'
import {SET_SHIPPING_ADDRESSES_ON_CART} from '_app/graphql/mutations/cart/SetShippingAddressOnCart'
import useCart from '_app/hooks/useCart'
import useCustomer from '_app/hooks/useCustomer'
import {GTM_EVENTS} from '_app/utils/dataLayer'
import {AddressFormData, STEPS} from 'modules/checkout/types'
import {useCheckoutContext} from '../..'
import {getBillingAddressVariables} from './billingAddress'
import {getShippingAddressVariables} from './shippingAddress'
import {addNoReceiptInfo} from './utils'
import {cartIdVar, emailOnCart} from '../../../../_app/cache'
import EventsHelper from '../../../../helpers/EventsHelper/EventsHelper'
import ConfigurationHelper from '../../../../helpers/ConfigurationHelper/ConfigurationHelper'
import {initializeApollo} from '../../../../helpers/ApolloHelper/ApolloHelper'
import {KLARNA_UPDATE_SESSION} from '../../../../_app/graphql/queries/klarna/updateSessionKlarna'
import {GetCartDocumentSmall} from '../../../../_app/graphql/queries/cart/GetCartDocumentSmall'
import {parseCookies} from 'nookies'
import LocalStorageHelper from '../../../../helpers/LocalStorageHelper/LocalStorageHelper'
import toast from '../../../../_app/utils/toast'
import {useRouter} from '../../../../_app/hooks/useRouter'
import CookiesHelper from '../../../../helpers/CookiesHelper/CookiesHelper'

interface UseAddressForm {
  setIsChecked: any;
  regionsAvailable: boolean;
  locale: any;
  t: any;
}

const useAddressForm = ({ setIsChecked, regionsAvailable, locale, t }: UseAddressForm) => {
  const customerNotExist = CookiesHelper.get('customerToken')?.length === 0 || !CookiesHelper.get('customerToken')
  const router = useRouter()
  const apolloClient = initializeApollo(null, { locale })
  const { setOrderComment, orderComment } = useCart()
  const { selectedShowroom, inpostAddress } = useCheckoutContext()
  const { setStep, setAreTermsAccepted } = useCheckoutContext()
  const [setBillingAddressOnCart] = useMutation(SET_BILLING_ADDRESS_ON_CART)
  const [setShippingAddressesOnCart] = useMutation(SET_SHIPPING_ADDRESSES_ON_CART)
  const [setGuestEmailOnCart] = useMutation(SET_GUEST_EMAIL_ON_CART)
  const onSubmit = async (data: AddressFormData) => {
    if (!selectedShowroom && data?.shipping_address?.selected_shipping_method?.method_code === 'showroom') {
      toast({ description: 'Proszę o wybranie punktu odbioru paczki.', status: 'error' })
      return null
    }
    if (!inpostAddress && data?.shipping_address?.selected_shipping_method?.carrier_code === 'inpostlocker') {
      toast({ description: 'Proszę o wybranie punktu odbioru paczki.', status: 'error' })
      return null
    }
    setIsChecked((prevVal: any) => {
      const {
        accept_regulations,
        subscribe_to_newsletter_email,
        receive_invoice,
        send_receipt_to_different_address,
      } = data
      setAreTermsAccepted(accept_regulations)
      if (accept_regulations === undefined && customerNotExist) return prevVal
      return {
        accept_regulations,
        subscribe_to_newsletter_email,
        receive_invoice,
        send_receipt_to_different_address,
      }
    })
    await setShippingAddressesOnCart({ variables: getShippingAddressVariables(data, regionsAvailable) })
    if (localStorage?.getItem('receiveInvoiceWatch') === 'true') {
      await setBillingAddressOnCart({
        variables: getBillingAddressVariables(data, true),
        refetchQueries: ['GetCart'],
      })
    } else {
      await setBillingAddressOnCart({
        variables: getBillingAddressVariables(data),
        refetchQueries: ['GetCart'],
      })
    }
    if (customerNotExist) {
      await setGuestEmailOnCart({
        variables: {
          email: data?.email,
        },
        refetchQueries: ['GetCart'],
      })
      await emailOnCart(data?.email)
    } else {
      if (data?.email) {
        await emailOnCart(data?.email)
      }
    }
    addNoReceiptInfo(
        orderComment,
        setOrderComment,
        data.send_receipt_to_different_address
    )
    if (ConfigurationHelper.getConfiguration('enable_klarna', router) === '1' && locale) {
      const { cartId } = parseCookies()
      const { data: dataCart } = await apolloClient.query({
        query: GetCartDocumentSmall,
        variables: { cartId },
      })
      const cart = dataCart?.cart
      const shipping = cart?.shipping_addresses?.[0]
      const inpostlocker = cart?.shipping_addresses?.[0]?.selected_shipping_method?.carrier_code === 'inpostlocker'
      const inpostAdress = LocalStorageHelper.get('inpostAdress')?.split(' ')
      let tempData: any = null
      const variables: any = {
        cartId
      }
      if (inpostAdress && inpostlocker) {
        tempData = {
          body: '',
          content_type: 'application/vnd.klarna.internal.emd-v2+json'
        }
        tempData.body = JSON.stringify({
          other_delivery_address: [{
            shipping_method: 'pick-up point',
            shipping_type: 'normal',
            first_name: shipping?.firstname,
            last_name: shipping?.lastname,
            street_address: inpostAdress[0],
            street_number: inpostAdress[1],
            postal_code: inpostAdress[2],
            city: inpostAdress[3],
            country: 'PL',
          }]
        })
        if (tempData) {
          variables.attachment = JSON.stringify(tempData)
        }
        const { data } = await apolloClient.query({
          query: KLARNA_UPDATE_SESSION,
          variables: variables
        })
        if (!data?.klarnaSessionUpdate) {
          LocalStorageHelper.set('disabledKlarna', 'true')
          toast({
            description: t('FAILED_TO_UPDATE_KLARNA_SESSION'),
            status: 'error',
          })
        } else {
          LocalStorageHelper.remove('disabledKlarna')
        }
      }
    }
    EventsHelper.scrollToTop()
    if (data.subscribe_to_newsletter_email) GTM_EVENTS.newsletter()
    setStep(STEPS.PAYMENT)
  }
  return {
    onSubmit,
  }
}

export default useAddressForm
